import {
  LogoutOutlined,
  PlusOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { outLogin } from '@/services/ant-design-pro/api';
import { clearAuthToken, setAuthToken } from '@/authToken';
import HeaderDropdown from '../HeaderDropdown';
import {
  getActiveAccountId,
  getLoggedInAccounts,
  removeLoggedInAccount,
  setActiveAccountId,
} from '@/loggedInAccounts';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await outLogin();
    clearAuthToken();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }

    if (key === 'add_account') {
      const { search, pathname } = window.location;
      const urlParams = new URL(window.location.href).searchParams;
      const redirect = urlParams.get('redirect');
      if (window.location.pathname !== '/user/login') {
        const nextRedirect = redirect || pathname + search;
        history.replace({
          pathname: '/user/login',
          search: new URLSearchParams({
            redirect: nextRedirect,
          }).toString(),
        });
      }
      return;
    }

    if (typeof key === 'string' && key.startsWith('switch_account:')) {
      const accountId = key.replace('switch_account:', '');
      const doSwitch = async () => {
        const list = getLoggedInAccounts();
        const account = list.find((a) => a.id === accountId);
        if (!account) return;

        flushSync(() => {
          setInitialState((s) => ({ ...s, currentUser: undefined }));
        });

        setAuthToken(account.token);
        setActiveAccountId(accountId);

        const userInfo = await initialState?.fetchUserInfo?.();
        if (userInfo) {
          flushSync(() => {
            setInitialState((s) => ({ ...s, currentUser: userInfo }));
          });
          // 切换账号后需要刷新页面，确保后续请求/界面状态基于新 token
          window.location.reload();
          return;
        }

        // token 失效或账号不可用：从本地列表移除，避免下一次切换继续失败
        removeLoggedInAccount(accountId);
      };
      void doSwitch();
      return;
    }

    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const loggedInAccounts = getLoggedInAccounts();
  const activeAccountId = getActiveAccountId();

  const switchAccountChildren = loggedInAccounts.map((acc) => {
    const label = acc.id === activeAccountId ? `${acc.label}（当前）` : acc.label;
    return {
      key: `switch_account:${acc.id}`,
      label,
      icon: <UserOutlined />,
    };
  });

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'add_account',
      icon: <PlusOutlined />,
      label: '新增账号',
    },
    ...(switchAccountChildren.length
      ? [
          {
            key: 'switch_account',
            icon: <UserOutlined />,
            label: '切换账号',
            children: switchAccountChildren,
          },
          {
            type: 'divider' as const,
          },
        ]
      : [
          {
            type: 'divider' as const,
          },
        ]),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
    </>
  );
};
